import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

import { Box, Stack, Typography } from "@mui/joy";
import { AutoAwesomeOutlined } from "@mui/icons-material";

import Message from "./Message";

import { FileViewerArgs } from "./fileviewer/FileViewer";

import { timeDifference } from "../util/dataUtil";
import { ApiMessage } from "../shared/interfaces";
import { comparatorMessageOldToNew } from "../util/objectUtil";

export type MessagesProps = {
    messages: ApiMessage[];
    loadingFromServer: boolean;
    waitingForReply: boolean;
    setCurrentFileViewerFile: Dispatch<SetStateAction<FileViewerArgs | null>>;
    currentFileViewerFile: FileViewerArgs | null;
};

const styles = {
    mainContainer: {
        display: "flex",
        flex: 1,
        minHeight: 0,
        py: 1,
        overflowY: "scroll",
        flexDirection: "column-reverse",
        scrollbarColor: "var(--joy-palette-neutral-400) white",
    },
    messagesStack: {
        width: "100%",
        maxWidth: "900px",
        px: 2,
        alignSelf: "center",
    },
    progressText: {
        background: `linear-gradient(90deg,var(--joy-palette-neutral-500) 0%,
                                           var(--joy-palette-neutral-500) 20%,
                                           var(--joy-palette-primary-300) 40%,
                                           var(--joy-palette-primary-500) 50%,
                                           var(--joy-palette-primary-300) 60%,
                                           var(--joy-palette-neutral-500)) 85%`,
        backgroundSize: "200% 100%",
        animation: "colorShift 3s linear infinite",
        WebkitBackgroundClip: "text",
        color: "transparent",
        "@keyframes colorShift": {
            "0%": { backgroundPosition: "200% 50%" },
            "100%": { backgroundPosition: "0% 50%" },
        },
    },
    progressContainer: { pb: 4 },
};

export default function Messages(props: MessagesProps) {
    const messages = props.messages.sort(comparatorMessageOldToNew);
    const loadingFromServer = props.loadingFromServer;

    const setCurrentFileViewerFile = props.setCurrentFileViewerFile;
    const currentFileViewerFile = props.currentFileViewerFile;

    // Timing to show how long the request is taking
    const [elapsedTime, setElapsedTime] = useState<string>("");

    useEffect(() => {
        let intervalId: NodeJS.Timeout | undefined;

        if (props.waitingForReply) {
            const start = new Date();
            intervalId = setInterval(() => {
                setElapsedTime(timeDifference(start, new Date()));
            }, 1000);
        } else {
            setElapsedTime("");
            if (intervalId) {
                clearInterval(intervalId);
            }
        }

        return () => clearInterval(intervalId);
    }, [props.waitingForReply]);

    return (
        <Box sx={styles.mainContainer}>
            <Stack spacing={4} sx={styles.messagesStack}>
                {messages.length === 0 && loadingFromServer && (
                    <Typography alignSelf="center" p={4} color="neutral" level="body-sm">
                        Loading ...
                    </Typography>
                )}

                {messages.length === 0 && !loadingFromServer && (
                    <Typography alignSelf="center" p={2} color="neutral" level="body-xs">
                        Ask Marvin anything 👇
                    </Typography>
                )}

                {messages.map((msg) => (
                    <Message
                        key={msg.id}
                        message={msg}
                        setCurrentFileViewerFile={setCurrentFileViewerFile}
                        currentFileViewerFile={currentFileViewerFile}
                    />
                ))}

                {props.waitingForReply && (
                    <Stack spacing={1} sx={styles.progressContainer}>
                        <Typography
                            color="neutral"
                            level="title-sm"
                            startDecorator={<AutoAwesomeOutlined />}
                        >
                            Marvin
                        </Typography>

                        <Typography sx={styles.progressText}>
                            I'm on it! This may take a moment&nbsp;
                            <small>{elapsedTime && `(${elapsedTime})`}</small> ...
                        </Typography>
                    </Stack>
                )}
            </Stack>
        </Box>
    );
}
